import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';

import { GetProductDetailQuery } from '../../common/gql/graphql';
import { ActiveService } from '../../../core/providers/active-order/active.service';
import { OptionsOrderingService } from '../../providers/options-ordering.service';
import { BaseVariantSelectorComponent } from '../base-variant-selector/base-variant-selector.component';
import { UppercaseFirstPipe } from '../../../shared/pipes/uppercase-first.pipe';
import { AddToWishlistButtonComponent } from '../add-to-wishlist-button/add-to-wishlist-button.component';
import { SelectedVariantControlsComponent } from '../selected-variant-controls/selected-variant-controls.component';
import { ShippingInfoLabelComponent } from '../../../shared/components/shipping-info-label/shipping-info-label.component';
import { FormsModule } from '@angular/forms';
import { NgFor, NgClass, NgIf, AsyncPipe } from '@angular/common';
import { qualifiesForLetterboxService } from "../../common/qualifies-for-letterbox-service";

type Variant = NonNullable<GetProductDetailQuery['product']>['variants'][number];
type OptionGroup = NonNullable<GetProductDetailQuery['product']>['optionGroups'][number];

@Component({
    selector: 'kb-variant-selector-default',
    templateUrl: './variant-selector-default.component.html',
    styleUrls: ['./variant-selector-default.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgFor,
        NgClass,
        NgIf,
        FormsModule,
        ShippingInfoLabelComponent,
        SelectedVariantControlsComponent,
        AddToWishlistButtonComponent,
        AsyncPipe,
        UppercaseFirstPipe,
    ],
})
export class VariantSelectorDefaultComponent
    extends BaseVariantSelectorComponent
    implements OnInit, OnChanges, OnDestroy
{
    @Output() selectedVariantChange = new EventEmitter<string>();
    selectedOptions: { [groupId: string]: string | undefined } = {};
    averageOptionSize: { [groupId: string]: number } = {};
    optionsAvailable: { [optionId: string]: boolean } = {};
    activeOptionGroupId: string | undefined;
    selectedVariant: Variant | undefined;
    qty = 1;
    selectOptionLabel = 'Select an option';
    displayOptionsWarning = false;
    missingOptionGroupIds: string[] = [];
    qualifiesForLetterboxService = false;
    private timer: any;

    constructor(
        protected optionsOrderingService: OptionsOrderingService,
        protected override activeService: ActiveService,
        protected override changeDetector: ChangeDetectorRef,
    ) {
        super(activeService, changeDetector);
    }

    override ngOnInit() {
        super.ngOnInit();
    }

    override ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);

        for (const group of this.product?.optionGroups ?? []) {
            this.selectedOptions[group.id] = undefined;
            this.averageOptionSize[group.id] =
                group.options.map((o) => o.name.length).reduce((sum, l) => sum + l, 0) / group.options.length;
        }
        this.qty = 1;
        this.selectedOptions = this.calculateAvailableOptions({});
        this.updateSelectedVariant();
        this.qualifiesForLetterboxService = qualifiesForLetterboxService(this.product?.variants, this.selectedVariant?.id);
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        clearTimeout(this.timer);
    }

    trackById(index: number, item: { id: string }) {
        return item.id;
    }

    focusOptionGroup(groupId: string) {
        this.activeOptionGroupId = groupId;
    }

    blurOptionGroup() {
        this.activeOptionGroupId = undefined;
    }

    sortedOptions(options: OptionGroup['options']): OptionGroup['options'] {
        return this.optionsOrderingService.sort(
            options,
            this.product?.customFields?.variantOrdering ?? ('default' as any),
        );
    }

    optionColGridClass(groupId: string): string {
        const optionSize = this.averageOptionSize[groupId] ?? 1;
        if (optionSize < 10) {
            return 'grid-cols-3 lg:grid-cols-4';
        } else if (optionSize < 20) {
            return 'grid-cols-2 lg:grid-cols-3';
        } else {
            return 'grid-cols-1 lg:grid-cols-2';
        }
    }

    checkAllOptionsSelected() {
        if (!this.selectedVariant) {
            this.displayOptionsWarning = true;
            clearTimeout(this.timer);
            this.timer = setTimeout(() => {
                this.displayOptionsWarning = false;
                this.changeDetector.markForCheck();
            }, 3000);
        }
    }

    variantIsOutOfStock(groupId: string, optionId: string): boolean {
        if (!this.product) {
            return false;
        }
        const groupIds = this.product.optionGroups.map((g) => g.id);
        const isLastGroup = groupIds.findIndex((g) => g === groupId) === groupIds.length - 1;
        if (!isLastGroup) {
            return false;
        }

        const variant = this.product.variants.find((v) => {
            for (const option of v.options) {
                if (option.groupId === groupId) {
                    if (option.id !== optionId) {
                        return false;
                    }
                } else if (this.selectedOptions[option.groupId] !== option.id) {
                    return false;
                }
            }
            return true;
        });
        return variant?.stockLevel === 'OUT_OF_STOCK' || false;
    }

    updateSelectedVariant() {
        this.selectedOptions = this.calculateAvailableOptions(this.selectedOptions);

        const variant = this.product?.variants.find((v) => {
            for (const option of v.options) {
                if (this.selectedOptions[option.groupId] !== option.id) {
                    return false;
                }
            }
            return true;
        });
        this.selectedVariant = variant;
        if (!variant) {
            const selectedGroupIds = Object.keys(this.selectedOptions);
            const missingGroups =
                this.product?.optionGroups.filter((og) => !selectedGroupIds.includes(og.id)) ?? [];
            this.selectOptionLabel = 'Select ' + missingGroups.map((g) => g.name).join(', ');
            this.missingOptionGroupIds = missingGroups.map((g) => g.id);
        } else {
            this.selectedVariantChange.emit(variant.id);
        }
        this.qualifiesForLetterboxService = qualifiesForLetterboxService(this.product?.variants, this.selectedVariant?.id);
    }

    private calculateAvailableOptions(selectedOptions: { [groupId: string]: string | undefined }): {
        [groupId: string]: string | undefined;
    } {
        const optionsPath: string[] = [];
        for (const optionGroup of this.product?.optionGroups ?? []) {
            const selectedOptionForThisGroup = selectedOptions[optionGroup.id];
            if (selectedOptionForThisGroup) {
                optionsPath.push(selectedOptionForThisGroup);
            }
            for (const option of optionGroup.options) {
                const idsToCheck = optionsPath.slice(0, -1).concat(option.id);
                const variantExistsForOption = this.variantExistsForOptions(idsToCheck);
                if (selectedOptions[optionGroup.id] === option.id && !variantExistsForOption) {
                    const firstAvailableOptionId = this.getFirstAvailableOptionId(
                        optionGroup,
                        optionsPath.slice(0, -1),
                    );
                    selectedOptions[optionGroup.id] = firstAvailableOptionId;
                    if (firstAvailableOptionId) {
                        optionsPath.splice(-1, 1, firstAvailableOptionId);
                    }
                }
                this.optionsAvailable[option.id] = variantExistsForOption;
            }
        }
        return selectedOptions;
    }

    private getFirstAvailableOptionId(
        optionGroup: OptionGroup,
        optionPath: string[] = [],
    ): string | undefined {
        for (const option of optionGroup.options) {
            const idsToCheck = [...optionPath, option.id];
            const variantExistsForOption = this.variantExistsForOptions(idsToCheck);
            if (variantExistsForOption) {
                return option.id;
            }
        }
        return;
    }

    private variantExistsForOptions(optionIds: string[]): boolean {
        return !!this.getVariantWithOptions(optionIds);
    }

    private getVariantWithOptions(optionIds: string[]): Variant | undefined {
        return this.product?.variants.find((v) => {
            for (const optionId of optionIds) {
                if (!v.options.find((_opt) => _opt.id === optionId)) {
                    return false;
                }
            }
            return true;
        });
    }
}
